import { useEffect, useMemo, useState, type FC } from 'react';
import {
  CreditCardOutlined,
  EnvironmentOutlined,
  MailOutlined,
  PhoneOutlined,
  RollbackOutlined,
  ShoppingOutlined,
  TruckOutlined,
} from '@ant-design/icons';

import type { Address } from '@eshop/types/location.types.ts';
import type { ImageMinimumInfo } from '@app-types/commons/images.types.ts';
import { buildGoogleMapsEmbedUrl } from '@tools/mapsEmbed.ts';
import type { SiteLocation, SiteSettings } from '@store/slices/settingsSlice.tsx';
import { buildCatalogueSocialLinks, CatalogueSocialIcon } from './catalogueSocial.tsx';
import {
  fetchFooterPublicData,
} from './footerPublicData.ts';
import type {
  PublicPaymentMethod,
  PublicShippingMethod,
} from './footerDataUtils.ts';
import { resolveFooterImage } from './footerImage.ts';
import { buildWhatsappHref, pickCancellationWhatsapp } from './footerPresentationUtils.ts';
import { useCheckoutLayout } from '@eshop/hooks/useCheckoutLayout.tsx';

const CANCELLATION_MESSAGE = 'Hola! Quisiera cancelar una orden';

const sanitizeTelHref = (value: string): string => value.replace(/[^+\d]/g, '');

const formatStoreAddress = (address?: Address | null): string | null => {
  if (!address) return null;
  const city = typeof address.city === 'string' ? address.city : address.verbose?.city;
  const state = typeof address.state === 'string' ? address.state : address.verbose?.state;
  const values = [
    address.street,
    address.addr_detail,
    [city, state].filter(Boolean).join(', '),
    address.verbose?.postcode ?? address.postcode,
    address.verbose?.country ?? address.country,
  ].filter(Boolean);
  return values.length ? values.join(', ') : null;
};

const buildMapsUrl = (text: string): string =>
  `https://maps.google.com/?q=${encodeURIComponent(text)}`;

const buildLocationMapUrl = (location: SiteLocation): string => {
  if (location.maps_url) return location.maps_url;
  if (Number.isFinite(location.lat) && Number.isFinite(location.lng)) {
    return buildMapsUrl(`${location.lat},${location.lng}`);
  }
  return buildMapsUrl(location.address);
};

const methodImageUrl = (image?: ImageMinimumInfo): string | null => {
  if (!image?.thumbnails) return null;
  return resolveFooterImage(image)?.srcDefault ?? null;
};

interface EShopFooterProps {
  settings: SiteSettings | null;
  visible?: boolean;
}

export const EShopFooter: FC<EShopFooterProps> = ({ settings, visible = true }) => {
  const { checkoutMode } = useCheckoutLayout();
  const [paymentMethods, setPaymentMethods] = useState<PublicPaymentMethod[]>([]);
  const [shippingMethods, setShippingMethods] = useState<PublicShippingMethod[]>([]);

  useEffect(() => {
    setPaymentMethods([]);
    setShippingMethods([]);
    if (!visible || !settings) return undefined;
    let cancelled = false;

    void fetchFooterPublicData()
      .then((data) => {
        if (cancelled) return;
        setPaymentMethods(data.paymentMethods);
        setShippingMethods(data.shippingMethods);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [settings, visible]);

  const brandName = settings?.name?.trim() || null;
  const description = settings?.description?.trim() || null;
  const logo = resolveFooterImage(settings?.logo);
  const email = settings?.contact_email?.trim() || null;
  const whatsappNumbers = settings?.whatsapp_numbers ?? [];
  const landlines = settings?.landline_numbers?.filter((value) => value.trim()) ?? [];
  const socialLinks = buildCatalogueSocialLinks(settings);
  const storeAddress = formatStoreAddress(settings?.store_address);
  const locations = settings?.locations ?? [];
  const cancellationWhatsapp = pickCancellationWhatsapp(whatsappNumbers);
  const cancellationHref = cancellationWhatsapp
    ? buildWhatsappHref(cancellationWhatsapp, CANCELLATION_MESSAGE)
    : null;

  const contactEntries = useMemo(() => [
    ...landlines.map((phone) => ({ key: `phone-${phone}`, label: phone, href: `tel:${sanitizeTelHref(phone)}`, Icon: PhoneOutlined })),
    ...whatsappNumbers.flatMap((entry) => {
      const href = buildWhatsappHref(entry);
      return href ? [{ key: `whatsapp-${entry.phone_e164}`, label: entry.label || entry.phone_e164, href, Icon: PhoneOutlined }] : [];
    }),
    ...(email ? [{ key: `email-${email}`, label: email, href: `mailto:${email}`, Icon: MailOutlined }] : []),
  ], [email, landlines, whatsappNumbers]);

  const hasBrand = Boolean(brandName || description || logo?.srcDefault);
  const hasLocations = Boolean(storeAddress || locations.length);
  const hasBottom = Boolean(brandName || settings?.canonical_base_url);
  const hasContent = hasBrand || contactEntries.length > 0 || socialLinks.length > 0
    || paymentMethods.length > 0 || shippingMethods.length > 0 || hasLocations || cancellationHref;

  if (!visible || !settings || !hasContent || checkoutMode) return null;

  return (
    <footer className="eshop-footer">
      <div className="eshop-footer__inner">
        <div className="eshop-footer__grid">
          {(hasBrand || contactEntries.length > 0 || socialLinks.length > 0 || cancellationHref) ? (
            <section className="eshop-footer__section eshop-footer__brand">
              {hasBrand ? (
                <div className="eshop-footer__brand-heading">
                  {logo?.srcDefault ? <img src={logo.srcDefault} srcSet={logo.srcSet} alt={brandName ? `Logo de ${brandName}` : 'Logo'} /> : <ShoppingOutlined aria-hidden />}
                  {brandName ? <h2>{brandName}</h2> : null}
                </div>
              ) : null}
              {description ? <p style={{ textAlign: 'left' }}>{description}</p> : null}
              {contactEntries.length ? (
                <ul className="eshop-footer__contact-list">
                  {contactEntries.map(({ key, label, href, Icon }) => (
                    <li key={key}><Icon aria-hidden /><a href={href} target={href.startsWith('https') ? '_blank' : undefined} rel="noopener noreferrer">{label}</a></li>
                  ))}
                </ul>
              ) : null}
              {socialLinks.length ? (
                <div className="eshop-footer__socials" aria-label="Redes sociales">
                  {socialLinks.map((link) => (
                    <a key={link.key} href={link.url} target="_blank" rel="noopener noreferrer" aria-label={link.label}>
                      <CatalogueSocialIcon link={link} size={19} />
                    </a>
                  ))}
                </div>
              ) : null}
              {cancellationHref ? (
                <a className="eshop-footer__withdrawal" href={cancellationHref} target="_blank" rel="noopener noreferrer">
                  <RollbackOutlined aria-hidden /> Arrepentimiento
                </a>
              ) : null}
            </section>
          ) : null}

          {(paymentMethods.length || shippingMethods.length) ? (
            <section className="eshop-footer__section eshop-footer__methods">
              {paymentMethods.length ? (
                <div>
                  <h3><CreditCardOutlined aria-hidden /> Medios de pago</h3>
                  <div className="eshop-footer__method-grid">
                    {paymentMethods.map((method) => {
                      const image = methodImageUrl(method.image);
                      return <div key={`payment-${method.id}`} className="eshop-footer__method">{image ? <img src={image} alt={method.name} /> : <span>{method.name}</span>}</div>;
                    })}
                  </div>
                </div>
              ) : null}
              {shippingMethods.length ? (
                <div>
                  <h3><TruckOutlined aria-hidden /> Medios de envío</h3>
                  <div className="eshop-footer__method-grid">
                    {shippingMethods.map((method) => {
                      const image = methodImageUrl(method.image);
                      return <div key={`shipping-${method.id}`} className="eshop-footer__method">{image ? <img src={image} alt={method.name} /> : <span>{method.name}</span>}</div>;
                    })}
                  </div>
                </div>
              ) : null}
            </section>
          ) : null}

          {hasLocations ? (
            <section className="eshop-footer__section eshop-footer__locations">
              <h3><EnvironmentOutlined aria-hidden /> Lugares de retiro</h3>
              {storeAddress ? (
                <address><a href={buildMapsUrl(storeAddress)} target="_blank" rel="noopener noreferrer">{storeAddress}</a></address>
              ) : null}
              {locations.map((location) => {
                let embedUrl: string | null = null;
                if (location.maps_embed_code) {
                  try {
                    embedUrl = buildGoogleMapsEmbedUrl(location.maps_embed_code);
                  } catch {
                    embedUrl = null;
                  }
                }
                return (
                  <article key={`${location.label}-${location.address}`} className="eshop-footer__location">
                    <strong>{location.label}</strong>
                    <a href={buildLocationMapUrl(location)} target="_blank" rel="noopener noreferrer">{location.address}</a>
                    {location.notes ? <p>{location.notes}</p> : null}
                    {embedUrl ? (
                      <iframe
                        src={embedUrl}
                        title={`Mapa de ${location.label}`}
                        loading="lazy"
                        allowFullScreen
                        referrerPolicy="no-referrer-when-downgrade"
                      />
                    ) : null}
                  </article>
                );
              })}
            </section>
          ) : null}
        </div>

        {hasBottom ? (
          <div className="eshop-footer__bottom">
            {brandName ? <span>© {new Date().getFullYear()} {brandName}</span> : null}
            {settings?.canonical_base_url ? <a href={settings.canonical_base_url} target="_blank" rel="noopener noreferrer">{settings.canonical_base_url.replace(/^https?:\/\//, '')}</a> : null}
          </div>
        ) : null}
      </div>
    </footer>
  );
};

export default EShopFooter;
